// =============================================================================
// services/api.ts — HTTP client, auth token store, and onboarding helpers
//
// Provides:
//   - Token storage (getToken/setToken/clearToken) backed by react-native-keychain
//     (via the SecureStore-shaped ./secure-store adapter — bare-RN swap, §1/#7).
//   - api<T>(): the core fetch wrapper. Injects the bearer token, parses bodies
//     defensively, and ALWAYS resolves to { success, data?, error? } — it never
//     throws, so callers can branch on `success` without try/catch.
//   - Auth flows (login/register/appleSignIn/getMe/logout) that persist the
//     access token on success.
//   - Onboarding helpers that hit /api/user (a different mount than /api/v1) via
//     apiUser(), plus profile/social/wallet/community helpers on the main API.
//   - Media upload (uploadMedia) + URL rewrite (toPublicMediaUrl).
//
// Envelope convention: the backend may wrap payloads as { data: ... } or return
// the payload bare; api() normalises both to result.data via (data?.data ?? data).
//
// Base URLs:
//   API_BASE   = https://api.cryb.ai/api/v1   (primary REST API)
//   API_ORIGIN = https://api.cryb.ai          (derived; used for /api/user)
// =============================================================================
// react-native-keychain via a SecureStore-shaped adapter. The adapter exposes the
// identical getItemAsync/setItemAsync/deleteItemAsync API, so the token-store code
// below is byte-for-byte the Expo version with only the import swapped (§1/#7).
import * as SecureStore from './secure-store';

const API_BASE = 'https://api.cryb.ai/api/v1';

// --- Auth token storage (keychain-backed via SecureStore-shaped adapter) ------
//
// We persist BOTH tokens the backend issues:
//   - access token  (`auth_token`)     — short-lived (~15 min), sent as Bearer.
//   - refresh token (`refresh_token`)  — long-lived (~30 days), used to mint a
//                                        new access token when the old one expires.
//
// The access token alone is NOT enough for a persistent session: it expires in
// minutes, after which every request 401s ("Token has expired"). Storing the
// refresh token lets api() transparently renew the session in the background so
// the user stays logged in for the full refresh-token lifetime.

const ACCESS_KEY = 'auth_token';
const REFRESH_KEY = 'refresh_token';

export async function getToken(): Promise<string | null> {
  try {
    return await SecureStore.getItemAsync(ACCESS_KEY);
  } catch {
    return null;
  }
}

export async function setToken(token: string): Promise<void> {
  await SecureStore.setItemAsync(ACCESS_KEY, token);
}

async function getRefreshToken(): Promise<string | null> {
  try {
    return await SecureStore.getItemAsync(REFRESH_KEY);
  } catch {
    return null;
  }
}

async function setRefreshToken(token: string): Promise<void> {
  try {
    await SecureStore.setItemAsync(REFRESH_KEY, token);
  } catch {
    /* non-fatal: refresh just won't be available until next login */
  }
}

// Persist whatever subset of { accessToken, refreshToken } the server returned.
// Used by every auth entry point (login/register/apple/refresh) so the two
// tokens never drift out of sync.
export async function storeTokens(tokens?: { accessToken?: string; refreshToken?: string }): Promise<void> {
  if (!tokens) return;
  if (tokens.accessToken) await setToken(tokens.accessToken);
  if (tokens.refreshToken) await setRefreshToken(tokens.refreshToken);
  if (tokens.accessToken) emitAuthChange(true);
}

export async function clearToken(): Promise<void> {
  try { await SecureStore.deleteItemAsync(ACCESS_KEY); } catch {}
  try { await SecureStore.deleteItemAsync(REFRESH_KEY); } catch {}
  // Clear the in-memory signup flag on logout so it can't leak into the NEXT
  // session and bounce a freshly-logged-in user into onboarding (#45).
  setOnboardingPending(false);
}

// --- Auth-change subscription -------------------------------------------------
//
// Lets the root layout react to login/logout that happen deep in the component
// tree (e.g. the Settings logout button or an auth screen). Without this, logout
// cleared the token but the layout's local `isAuthed` stayed true — so the gate
// kept the user inside the app shell and the now-tokenless screens fired API
// calls that 401'd with "Authorization header missing".
type AuthListener = (authed: boolean) => void;
const authListeners = new Set<AuthListener>();
export function onAuthChange(listener: AuthListener): () => void {
  authListeners.add(listener);
  return () => { authListeners.delete(listener); };
}
function emitAuthChange(authed: boolean): void {
  authListeners.forEach((l) => { try { l(authed); } catch {} });
}

// --- Onboarding routing flag --------------------------------------------------
// When a brand-new user signs up, storeTokens() flips isAuthed BEFORE the screen
// can navigate to onboarding, so the auth gate can race and bounce them to home.
// This module flag makes routing deterministic: the gate sends an authed user in
// the (auth) group to ONBOARDING (not home) while it is set. Cleared when
// onboarding finishes. Module-scoped (resets on app relaunch), which is fine —
// it only matters during the signup→onboarding transition.
let _onboardingPending = false;
export function setOnboardingPending(v: boolean): void { _onboardingPending = v; }
export function isOnboardingPending(): boolean { return _onboardingPending; }

// --- Last upload error (diagnostics) ------------------------------------------
// uploadMedia returns null on any failure; callers historically showed a generic
// "check your connection" alert that hid the real cause. We stash the real reason
// here so screens can surface it (HTTP status / server message / network error).
let _lastUploadError: string | null = null;
export function getLastUploadError(): string | null { return _lastUploadError; }

// --- Silent token refresh -----------------------------------------------------
//
// Exchanges the stored refresh token for a fresh access (and rotated refresh)
// token via POST /auth/refresh. Returns the new access token, or null if there
// is no refresh token / the refresh was rejected (expired/revoked → real logout).
//
// `refreshInFlight` de-dupes concurrent refreshes: if several requests 401 at
// once they all await the SAME refresh call instead of stampeding the endpoint.
let refreshInFlight: Promise<string | null> | null = null;

export async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const refreshToken = await getRefreshToken();
    if (!refreshToken) return null;
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CRYB-Client': 'native' },
        body: JSON.stringify({ refreshToken }),
        credentials: 'omit',
      });
      // ONLY a definitive 401/403 means the refresh token is truly invalid →
      // real logout. Any other non-2xx (5xx, 429, proxy hiccup on the sim's
      // flaky network) is TRANSIENT — throw so the caller keeps the session
      // instead of ejecting a user whose refresh token is still valid. This was
      // the "Profile logs me out" cause: a transient refresh returned null → eject.
      if (res.status === 401 || res.status === 403) return null;
      if (!res.ok) throw new Error(`refresh HTTP ${res.status}`);
      const json = await res.json().catch(() => null);
      const tokens = json?.data?.tokens ?? json?.tokens;
      if (tokens?.accessToken) {
        await storeTokens(tokens);
        return tokens.accessToken as string;
      }
      // 2xx but no token in the body → ambiguous, NOT proof the session died.
      // Throw (keep session) rather than eject.
      throw new Error('refresh: no accessToken in response');
    } catch (e) {
      // Network error during refresh — this is NOT proof the session is over.
      // Re-throw so the caller's try/catch surfaces a transient error and the
      // tokens are KEPT (returning null here would make callers clearToken()
      // and eject a user whose refresh token is still perfectly valid).
      throw e;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

// --- Request timeout (LR9 — kill the infinite-spinner class) ------------------
//
// On low/3G/flaky connectivity a bare fetch() can hang for minutes (no native
// default timeout), which surfaces as a screen stuck on a spinner forever. We
// race every request against an AbortController so a stalled request fails fast
// with a clear, retryable error instead of hanging. 20s is generous enough for a
// slow-but-alive 3G round-trip while still bounding the worst case. Callers can
// override per-request via options.signal (we won't override an explicit one).
const DEFAULT_TIMEOUT_MS = 20000;

// fetch() with a hard timeout. If the caller already passed a signal we respect
// it and skip our own (don't fight a caller-managed abort). Returns the Response
// or throws (AbortError on timeout) — callers already turn throws into
// { success:false, error }.
async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<Response> {
  if (init.signal) return fetch(url, init);
  const controller = new AbortController();
  const timer = setTimeout(() => {
    try { controller.abort(); } catch {}
  }, timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

// --- Core request wrapper -----------------------------------------------------

// Fetches `${API_BASE}${path}`, attaching JSON headers + bearer token. Resolves
// to a result envelope rather than throwing; on non-2xx it extracts the server's
// error/message (falling back to "HTTP <status>"), and network failures become
// { success:false, error }. On success, unwraps a { data } envelope if present.
export async function api<T = any>(
  path: string,
  options: RequestInit = {},
  // Internal: prevents an infinite refresh loop. The retry-after-refresh call
  // sets this so a second 401 doesn't trigger another refresh.
  _isRetry = false
): Promise<{ success: boolean; data?: T; error?: string; code?: string; details?: Record<string, any>; silent?: boolean }> {
  const token = await getToken();
  // Auth endpoints must be EXEMPT from the generic 401→refresh/SESSION_EXPIRED
  // handling below: a 401 from /auth/login means "invalid email or password"
  // (and from register/forgot/reset/refresh, their own real errors) — NOT that
  // the current session expired. Blanket-converting those to the SESSION_EXPIRED
  // sentinel made a normal failed login show "Login failed: SESSION_EXPIRED" and
  // fire a bogus logout. Let these fall through to the real server error body.
  const isAuthEndpoint = /^\/auth\/(login|register|forgot|reset|refresh|verify)/.test(path);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    // Native-client marker: this app is Bearer-only and never a cookie-authed
    // browser. The server exempts requests carrying this header from the CSRF
    // Origin check (a browser can't set a custom header cross-site without a CORS
    // preflight, which the origin allowlist rejects — so it stays CSRF-safe). This
    // is what stops the intermittent "Cross-site request blocked: missing/invalid
    // Origin" 403 when RN's cookie jar replays a stale cryb_access cookie.
    'X-CRYB-Client': 'native',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers as Record<string, string> || {}),
  };
  // ORIGIN HARDENING (native): this client authenticates with a Bearer token and
  // never a cookie, so it must never look like a cross-site browser request. Some
  // RN runtimes / dev proxies inject a browser-like `Origin` header (e.g.
  // http://localhost) which is NOT on the server allowlist — that can trip the
  // CSRF Origin check on any request that DOES carry a cookie, and is pure noise
  // otherwise. Explicitly delete any Origin the platform added so unauthenticated
  // calls (login/register/forgot/reset) always present as a clean API client.
  delete (headers as any).Origin;
  delete (headers as any).origin;

  try {
    const res = await fetchWithTimeout(`${API_BASE}${path}`, {
      ...options,
      headers,
      // Bearer-only client: never send/accept cookies, so RN's shared cookie jar
      // can't replay a stale cryb_access cookie and trip the server CSRF check.
      credentials: 'omit',
    });

    // On a 401 (expired/invalid access token), try ONCE to silently refresh the
    // session and replay the original request. This is what keeps users logged
    // in past the ~15-min access-token lifetime — without it every screen would
    // eventually fail with "Token has expired". Only attempt when we actually
    // had a token (i.e. an authenticated call) and aren't already retrying.
    if (res.status === 401 && token && !_isRetry && !isAuthEndpoint) {
      let newToken: string | null = null;
      try {
        newToken = await refreshAccessToken();
      } catch {
        // TRANSIENT refresh failure (5xx/503/429/network — refreshAccessToken only
        // throws on non-definitive errors now). The session is NOT over: keep the
        // tokens, do NOT eject, and return a SILENT sentinel so the caller shows
        // its own empty/retry state instead of a scary "Error: refresh HTTP 503"
        // alert while navigating. A later call (or the token still being valid)
        // recovers on its own.
        return { success: false, error: 'NETWORK', code: 'REFRESH_TRANSIENT', silent: true } as any;
      }
      if (newToken) {
        return api<T>(path, options, true);
      }
      // Refresh failed (no/expired refresh token) → the session is truly over.
      // Clear the dead tokens AND notify the auth gate so it routes to Login.
      // Without emitAuthChange(false) the user is stranded in the authed shell
      // with an empty keychain, and EVERY subsequent call fires unauthenticated
      // → server "Authorization header missing" (dead Chat/Profile/interactions).
      await clearToken();
      emitAuthChange(false);
      // Return a SILENT auth-expiry sentinel — do NOT fall through to the raw
      // 401 body ("Authorization header missing"), which a mid-flight background
      // loader (e.g. Chat) would surface as a jarring "Error" alert ON TOP of the
      // login screen we just routed to. Callers detect code AUTH_EXPIRED and skip
      // the alert (isAuthExpired). See the auth-gate comment above.
      return { success: false, error: 'SESSION_EXPIRED', code: 'AUTH_EXPIRED', silent: true } as any;
    }
    // Unauthenticated 401 (no token present): there is NO session to end, so do
    // NOT emitAuthChange(false) here. A background poller (e.g. the notifications
    // unread-count) firing while getToken() is momentarily null — a cold-start
    // race, or a flaky keychain read on the adhoc sim — used to hit this branch
    // and force a logout, ejecting a user whose session was actually fine
    // ("Profile logs me out"). The auth gate (useAuth) already owns routing to
    // Login when there's genuinely no token; here we just return the silent
    // sentinel so nothing shows the raw "Authorization header missing".
    if (res.status === 401 && !token && !isAuthEndpoint) {
      return { success: false, error: 'SESSION_EXPIRED', code: 'AUTH_EXPIRED', silent: true } as any;
    }

    // Body may be empty (204) or non-JSON (HTML error page / proxy).
    // Parse defensively so a bad body never crashes the caller.
    let data: any = null;
    const raw = await res.text();
    if (raw) {
      try {
        data = JSON.parse(raw);
      } catch {
        data = null;
      }
    }

    if (!res.ok) {
      let errorMsg = data?.error || data?.message;
      if (!errorMsg && Array.isArray(data?.errors) && data.errors.length > 0) {
        errorMsg = data.errors.map((e: any) => e?.message || e).join('. ');
      }
      if (!errorMsg && data?.details) {
        errorMsg = typeof data.details === 'string' ? data.details : JSON.stringify(data.details);
      }
      return {
        success: false,
        error: errorMsg || `HTTP ${res.status}`,
        code: data?.code,
        // Surface the server's structured `details` (retryAfter, fieldErrors,
        // passwordRules, …) so callers can render exact retry times + inline
        // per-field errors instead of only a flat message string.
        details: data?.details && typeof data.details === 'object' ? data.details : undefined,
      };
    }

    return { success: true, data: (data?.data ?? data) as T };
  } catch (err: any) {
    // An AbortError here means OUR timeout fired (slow/dead connection), not a
    // server error — surface a clear, retryable message instead of a cryptic
    // "Aborted" so screens can show "tap to retry" rather than an infinite spinner.
    const aborted = err?.name === 'AbortError';
    return {
      success: false,
      error: aborted ? 'Request timed out. Check your connection and try again.' : (err?.message || 'Network error'),
    };
  }
}

// --- Auth flows (persist access + refresh tokens on success) -----------------

export async function login(identifier: string, password: string) {
  const result = await api('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ identifier, password }),
  });

  if (result.success && result.data?.tokens) {
    await storeTokens(result.data.tokens);
  }

  return result;
}

export async function register(username: string, email: string, password: string) {
  const result = await api('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ username, email, password }),
  });

  if (result.success && result.data?.tokens) {
    await storeTokens(result.data.tokens);
  }

  return result;
}

export async function appleSignIn(identityToken: string, fullName?: any, email?: string, rawNonce?: string) {
  // Apple only returns fullName/email on the FIRST authorization (sign up). On
  // sign IN they are null — omit them so we never send null (backend schema
  // rejects fullName:null with a 400, which broke sign-in).
  //
  // The raw Apple fullName object includes nullable fields (givenName,
  // familyName, middleName, etc.). Strip nulls so the backend receives only
  // the fields Apple actually provided, preventing validation errors from
  // null values inside a non-null parent object.
  // The RAW nonce backs the identity token's hashed `nonce` claim. The backend
  // verifies SHA256(rawNonce) matches the token, defeating replay — send it
  // when present (Apple flow generates it; legacy callers may omit it).
  const body: Record<string, any> = { identityToken };
  if (rawNonce) body.rawNonce = rawNonce;
  if (fullName) {
    const cleaned: Record<string, string> = {};
    if (fullName.givenName) cleaned.givenName = fullName.givenName;
    if (fullName.familyName) cleaned.familyName = fullName.familyName;
    if (fullName.middleName) cleaned.middleName = fullName.middleName;
    if (Object.keys(cleaned).length > 0) body.fullName = cleaned;
  }
  if (email) body.email = email;
  const result = await api('/auth/apple', {
    method: 'POST',
    body: JSON.stringify(body),
  });

  if (result.success && result.data?.tokens) {
    await storeTokens(result.data.tokens);
  }

  return result;
}

export async function getMe() {
  return api('/auth/me');
}

// True when a call failed only because the session ended (token expired / missing).
// The auth gate has ALREADY been routed to Login by api(); screens use this to
// SKIP their "Error" alerts so the user never sees a raw auth error land on top of
// the login screen. Use in every background/mount-time loader's failure branch.
export function isAuthExpired(res: { code?: string; silent?: boolean } | null | undefined): boolean {
  return !!res && (res.code === 'AUTH_EXPIRED' || res.silent === true);
}

// --- Password policy (client mirror of the server's PASSWORD_RULES) -----------
// Kept byte-for-byte in sync with apps/api/src/utils/password.ts PASSWORD_RULES.
// The Register screen renders these live and validates optimistically before a
// round-trip; the server remains the source of truth (returns `passwordRules` +
// per-field errors on WEAK_PASSWORD). Each rule carries a pure `test` so the UI
// can show a live pass/fail per requirement.
export type PasswordRule = { key: string; label: string; test: (pw: string) => boolean };
export const PASSWORD_RULES: { minLength: number; rules: PasswordRule[] } = {
  minLength: 8,
  rules: [
    { key: 'minLength', label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
    { key: 'uppercase', label: 'One uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
    { key: 'lowercase', label: 'One lowercase letter', test: (pw) => /[a-z]/.test(pw) },
    { key: 'number', label: 'One number', test: (pw) => /[0-9]/.test(pw) },
    { key: 'special', label: 'One special character', test: (pw) => /[^A-Za-z0-9]/.test(pw) },
  ],
};

// Returns the keys of the rules a candidate password fails (empty = all pass).
export function passwordRuleFailures(pw: string): string[] {
  return PASSWORD_RULES.rules.filter((r) => !r.test(pw)).map((r) => r.key);
}

// --- Password-reset OTP flow (6-digit code by email) --------------------------
//
// The reset flow is two calls:
//   1. requestPasswordReset(email)                 → POST /auth/forgot-password
//        Server issues a single-use 6-digit code (purpose 'password_reset',
//        10-min TTL) and emails it. Always resolves success (anti-enumeration).
//   2. resetPasswordWithCode(email, code, pw, pw2) → POST /auth/reset-password
//        Server verifies the code + sets the new password.
// Both return the standard { success, data?, error?, code?, details? } envelope,
// so callers can read details.fieldErrors.code / details.retryAfter.

export async function requestPasswordReset(email: string) {
  return api('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email: email.trim() }),
  });
}

export async function resetPasswordWithCode(
  email: string,
  code: string,
  password: string,
  confirmPassword: string,
) {
  return api('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({
      email: email.trim(),
      code: code.trim(),
      password,
      confirmPassword,
    }),
  });
}

// --- Email-verification OTP flow (6-digit code) -------------------------------
//
// Separate from password reset: these verify an UNVERIFIED account's email.
//   requestEmailOtp(email) → POST /auth/request-email-otp  (re)sends the code.
//   verifyEmailOtp(email, code) → POST /auth/verify-email-otp  marks verified.
// Same envelope; verify surfaces details.fieldErrors.code on a bad/expired code.

export async function requestEmailOtp(email: string) {
  return api('/auth/request-email-otp', {
    method: 'POST',
    body: JSON.stringify({ email: email.trim() }),
  });
}

export async function verifyEmailOtp(email: string, code: string) {
  return api('/auth/verify-email-otp', {
    method: 'POST',
    body: JSON.stringify({ email: email.trim(), code: code.trim() }),
  });
}

// ---------------------------------------------------------------------------
// Onboarding helpers. Onboarding routes are mounted under /api/user (not /api/v1).
// All calls are crash-safe and return { success, data?, error? } like api().
// ---------------------------------------------------------------------------
const API_ORIGIN = API_BASE.replace(/\/api\/v1$/, '');

async function apiUser<T = any>(
  path: string,
  options: RequestInit = {},
  // Internal: see api() — prevents an infinite refresh loop on a repeated 401.
  _isRetry = false
): Promise<{ success: boolean; data?: T; error?: string }> {
  const token = await getToken();
  try {
    const res = await fetch(`${API_ORIGIN}/api/user${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-CRYB-Client': 'native',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers as Record<string, string> || {}),
      },
      credentials: 'omit',
    });

    // Same silent refresh-and-retry as api(): the onboarding routes are JWT-gated
    // too, so an expired access token here would otherwise fail onboarding steps.
    if (res.status === 401 && token && !_isRetry) {
      const newToken = await refreshAccessToken();
      if (newToken) return apiUser<T>(path, options, true);
      await clearToken();
    }

    let data: any = null;
    const raw = await res.text();
    if (raw) { try { data = JSON.parse(raw); } catch { data = null; } }
    if (!res.ok) return { success: false, error: data?.error || `HTTP ${res.status}` };
    return { success: true, data: (data?.data ?? data) as T };
  } catch (err: any) {
    return { success: false, error: err?.message || 'Network error' };
  }
}

// Recommended communities based on selected interests.
export async function recommendCommunities(interests: string[]) {
  return apiUser('/recommend-communities', {
    method: 'POST',
    body: JSON.stringify({ interests }),
  });
}

// Mark onboarding complete (records completed/skipped steps server-side).
export async function completeOnboarding(completedSteps: string[], skippedSteps: string[] = []) {
  return apiUser('/complete', {
    method: 'POST',
    body: JSON.stringify({ completedSteps, skippedSteps }),
  });
}

// --- Username selection (onboarding) -----------------------------------------
// Mirrors the server rule (validation.ts user.update): 3–32 chars, letters /
// digits / underscore / hyphen, lowercased server-side. Returns a human error or
// null when the candidate is locally valid. Pure (no network) so the UI can
// validate on every keystroke before spending an availability request.
export function validateUsername(raw: string): string | null {
  const v = (raw || '').trim();
  if (!v) return 'Pick a username.';
  if (v.length < 3) return 'Username must be at least 3 characters.';
  if (v.length > 32) return 'Username must be at most 32 characters.';
  if (!/^[a-zA-Z0-9_-]+$/.test(v)) return 'Only letters, numbers, _ and - are allowed.';
  return null;
}

// Availability check against GET /users/@:username (hard-auth — the onboarding
// user is signed in, so the token is sent automatically by api()). A 200 means
// the handle is taken; a 404 means it's free. Anything else (network/5xx) is
// reported as `unknown` so the caller can let the user proceed and rely on the
// authoritative uniqueness check in setUsername() rather than blocking on a
// flaky probe. `available:true` also returned when the candidate equals the
// user's CURRENT username (passed in `currentUsername`) — keeping your own name
// must never read as "taken".
export async function checkUsernameAvailable(
  username: string,
  currentUsername?: string
): Promise<{ available: boolean; unknown?: boolean }> {
  const u = (username || '').trim();
  if (currentUsername && u.toLowerCase() === currentUsername.trim().toLowerCase()) {
    return { available: true };
  }
  const res = await api(`/users/@${encodeURIComponent(u.toLowerCase())}`);
  if (res.success) {
    // Defensive: a 200 with an empty/!user body would otherwise read as taken.
    // Only treat it as taken when the payload actually carries a user.
    const d: any = res.data;
    const user = d?.user ?? d;
    const found = !!(user && (user.id || user.username));
    return { available: !found };
  }
  // api() turns a 404 into { success:false, error:'HTTP 404' | <server msg> }.
  const msg = (res.error || '').toLowerCase();
  if (msg.includes('404') || msg.includes('not found')) return { available: true };
  // Network error / 5xx / rate-limit → don't claim taken; let setUsername decide.
  return { available: false, unknown: true };
}

// Claim a username for the signed-in user (PATCH /users/me). The server is the
// source of truth for uniqueness ("Username already taken" → 400). Returns the
// same { success, data?, error? } envelope as api().
export async function setUsername(username: string) {
  return updateMe({ username: username.trim().toLowerCase() });
}

// Link a social account (handle capture). Real endpoint: POST /settings/social/link.
export async function linkSocial(platform: string, handle?: string) {
  return api('/settings/social/link', {
    method: 'POST',
    body: JSON.stringify({ platform, ...(handle ? { handle } : {}) }),
  });
}

// Persist a connected wallet address. Real endpoint: POST /wallet/connect.
export async function connectWalletAddress(address: string) {
  return api('/wallet/connect', {
    method: 'POST',
    body: JSON.stringify({ address }),
  });
}

// Join (follow) a community by name. Real endpoint: POST /communities/:name/join.
export async function joinCommunity(name: string) {
  return api(`/communities/${encodeURIComponent(name)}/join`, { method: 'POST' });
}

// Update the current user's profile (e.g. avatar URL after upload).
export async function updateMe(patch: Record<string, any>) {
  return api('/users/me', { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function logout() {
  // Best-effort SERVER-SIDE cleanup, HARD-BOUNDED so a slow/DOWN API can never
  // hang the logout. Previously these two calls were awaited sequentially, so
  // when the API was unreachable each waited its full request timeout (~15-20s)
  // → logout got "stuck on the spinner" for 30-40s+. Now the whole network phase
  // races a 2.5s cap; whatever hasn't finished is abandoned. Local teardown below
  // ALWAYS runs, so the user is signed out instantly regardless of the network.
  //   - unregisterPush() first: /notifications/push/unregister needs the Bearer
  //     token (before clearToken) or the signed-out device keeps getting pushes.
  await Promise.race([
    (async () => {
      try { await require('./push').unregisterPush(); } catch {}
      try { await api('/auth/logout', { method: 'POST' }); } catch {}
    })(),
    new Promise<void>((resolve) => { setTimeout(resolve, 2500); }),
  ]);
  // Tear down the real-time socket BEFORE clearing tokens. The socket singleton
  // holds the current user's auth + DM handlers; if left alive, the next user to
  // log in on this app session would inherit the previous user's live socket
  // (cross-session message bleed). Lazy require() avoids a circular import
  // (socket.ts imports from this module).
  try {
    require('./socket').disconnectSocket();
  } catch {}
  // Clear E2E key caches so the next login session never inherits the previous
  // user's key material (shared keys, cached keypair). Lazy require() avoids a
  // circular import (e2e.ts imports from this module).
  try {
    require('./e2e').clearE2ECache();
  } catch {}
  await clearToken();
  // Notify the root layout so it leaves the app shell for the login screen.
  emitAuthChange(false);
}

// =============================================================================
// Media upload (consolidated) — A4
//
// ONE service drives every upload surface (avatar, banner, post, story, channel,
// DM attachment, NFT). It routes each call to the correct API-contract endpoint
// (each has its own size cap + processing), sends multipart/form-data with the
// Bearer token (the /uploads/* plugin is behind a GLOBAL auth hook → 401 without
// it), refreshes the access token once on 401, normalises HEIC/HEIF (iOS default)
// to a usable filename + content-type, and parses the response defensively across
// every envelope shape the backend has shipped.
//
// Backward compatibility: uploadMedia(uri) keeps its string|null signature so the
// existing call sites (create-post, edit-profile, story, dm, nft, onboarding)
// need no change — it now delegates to the robust core.
// =============================================================================

// The /uploads/* endpoints, keyed by purpose. Each has its own size cap and
// server-side processing (avatar is resized to 512²; media accepts video/audio).
// `field` is the multipart field name the route expects ("file" for all today).
export type UploadKind = 'avatar' | 'attachment' | 'media' | 'document';

type UploadEndpoint = { path: string; maxBytes: number };
const UPLOAD_ENDPOINTS: Record<UploadKind, UploadEndpoint> = {
  avatar:     { path: '/uploads/avatar',     maxBytes: 5  * 1024 * 1024 },  // images only, resized 512²
  attachment: { path: '/uploads/attachment', maxBytes: 50 * 1024 * 1024 },  // images+docs+zip
  media:      { path: '/uploads/media',       maxBytes: 100 * 1024 * 1024 }, // image/video/audio
  document:   { path: '/uploads/document',    maxBytes: 25 * 1024 * 1024 },
};

// Full result envelope from a successful upload. `thumbnailUrl` is present for
// processed images; `size`/`contentType` feed POST /messages attachments[].
export type UploadResult = {
  url: string;
  thumbnailUrl?: string;
  filename: string;
  size?: number;
  contentType: string;
};

// Infer MIME from extension. Covers images (default), the iOS HEIC/HEIF capture
// format, video, plus audio formats so voice messages (.m4a/.mp3/.wav/...) upload
// with a correct Content-Type instead of being mislabeled as image/jpeg.
const MIME_BY_EXT: Record<string, string> = {
  // images
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  heic: 'image/heic',
  heif: 'image/heif',
  bmp: 'image/bmp',
  // video
  mov: 'video/quicktime',
  webm: 'video/webm',
  m4v: 'video/x-m4v',
  // audio
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  caf: 'audio/x-caf',
  '3gp': 'audio/3gpp',
  // documents
  pdf: 'application/pdf',
  zip: 'application/zip',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  txt: 'text/plain',
};

// `.mp4` is ambiguous (audio voice-note vs. video). Resolve by upload kind:
// for media uploads treat it as video; otherwise (voice messages on `media`
// historically) the caller can pass an explicit contentType to override.
function inferContentType(ext: string, explicit?: string): string {
  if (explicit) return explicit;
  if (ext === 'mp4') return 'video/mp4';
  return MIME_BY_EXT[ext] || 'image/jpeg';
}

// Derive a usable { filename, ext, contentType } from a local URI. iOS picks
// frequently yield extension-less cache URIs or HEIC; we always produce a
// concrete filename + content-type so the server's (fail-open) processImage and
// content-type sniffing get a usable hint rather than octet-stream.
function describeFile(uri: string, explicitContentType?: string): {
  filename: string;
  contentType: string;
} {
  let filename = uri.split('?')[0].split('/').pop() || '';
  let ext = (filename.includes('.') ? filename.split('.').pop() : '')!.toLowerCase();

  if (!ext) {
    // No extension on the URI — fall back to jpg (most common picker output).
    ext = 'jpg';
    filename = `upload-${Date.now()}.jpg`;
  }
  const contentType = inferContentType(ext, explicitContentType);
  if (!filename) filename = `upload-${Date.now()}.${ext}`;
  return { filename, contentType };
}

// Pull a public URL out of ANY response shape the backend has shipped. Contract
// is { success, data:{ original:{url}, thumbnail:{url} } }, but older/local-fallback
// paths returned { data:{ url } } / { url } / { fileUrl } — try them all. Guarded
// per the Prisma-drift recurring-bug class (never assume a single key exists).
function extractUploadResult(json: any, fallbackName: string, fallbackType: string): UploadResult | null {
  if (!json || typeof json !== 'object') return null;
  // data may be the result object, or an array of results (multi-file routes).
  const node = Array.isArray(json?.data) ? json.data[0] : (json?.data ?? json);
  if (!node || typeof node !== 'object') return null;

  const original = node.original && typeof node.original === 'object' ? node.original : node;
  const rawUrl = original?.url || node?.url || node?.fileUrl || node?.location || '';
  const url = toPublicMediaUrl(rawUrl);
  if (!url) return null;

  const thumbRaw = node?.thumbnail?.url || node?.thumbnailUrl || '';
  const thumbnailUrl = thumbRaw ? toPublicMediaUrl(thumbRaw) : undefined;

  return {
    url,
    thumbnailUrl,
    filename: original?.filename || node?.filename || fallbackName,
    size: typeof original?.size === 'number' ? original.size : (typeof node?.size === 'number' ? node.size : undefined),
    contentType: original?.contentType || node?.contentType || node?.mimeType || fallbackType,
  };
}


// Passthrough — NO client-side transcode dependency.
// We removed `expo-image-manipulator`: package.json pinned ^56 but the only build on
// disk was 12.0.5 (an Expo-51 release) — a version + SDK mismatch that NEVER linked
// natively, so calling it HARD-CRASHED every image upload (avatar + post). Image sizing
// is handled the native, already-linked way: `expo-image-picker`'s `quality` option
// compresses at pick time, and the server resizes/normalizes (HEIC included). So this
// just returns the original file — no native add-on, no landmine.
async function transcodeForUpload(
  uri: string,
  filename: string,
  contentType: string,
): Promise<{ uri: string; filename: string; contentType: string }> {
  return { uri, filename, contentType };
}

// A single multipart POST attempt. Does NOT set Content-Type — fetch must add the
// multipart boundary itself. FormData is single-use, so the caller rebuilds it.
async function uploadAttempt(
  url: string,
  token: string | null,
  buildForm: () => FormData
): Promise<{ res: Response; json: any }> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: buildForm(),
  });
  let json: any = null;
  try { json = await res.json(); } catch { json = null; }
  return { res, json };
}

// The robust core. Uploads a local file to the endpoint for `kind`, returning the
// full result envelope or null (with the reason in getLastUploadError). Sends the
// Bearer token, refreshes once on 401, and surfaces the REAL failure (HTTP status
// + server message / network error) instead of a generic "check your connection".
export async function uploadFile(
  uri: string,
  opts: { kind?: UploadKind; field?: string; contentType?: string } = {}
): Promise<UploadResult | null> {
  const kind: UploadKind = opts.kind || 'media';
  const endpoint = UPLOAD_ENDPOINTS[kind];
  const field = opts.field || 'file';
  const described = describeFile(uri, opts.contentType);

  // Transcode HEIC/HEIF → JPEG + downscale oversized images BEFORE upload so the
  // result renders for EVERY user (not just iOS), and large camera captures don't
  // trip the per-kind size cap. No-ops for video/audio/docs and when the manipulator
  // dep is absent. We upload the (possibly) transcoded file from here on.
  const { uri: sendUri, filename, contentType } =
    await transcodeForUpload(uri, described.filename, described.contentType);

  const buildForm = () => {
    const fd = new FormData();
    fd.append(field, { uri: sendUri, name: filename, type: contentType } as any);
    return fd;
  };

  // Remember the ORIGINAL inputs so retryLastUpload() replays from source (it will
  // re-transcode), not from a transient manipulator cache uri that may be evicted.
  _lastUploadInput = { uri, opts };

  try {
    let token = await getToken();
    let { res, json } = await uploadAttempt(`${API_BASE}${endpoint.path}`, token, buildForm);

    // An expired access token surfaces as a 401 here too — refresh once + retry so
    // uploads survive the ~15-min access-token lifetime like every other call. The
    // /uploads/* plugin is behind a global auth hook, so a missing/expired token is
    // a hard 401 (not a soft skip).
    if (res.status === 401 && token) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        ({ res, json } = await uploadAttempt(`${API_BASE}${endpoint.path}`, newToken, buildForm));
      } else {
        await clearToken();
        emitAuthChange(false);
      }
    }

    const result = extractUploadResult(json, filename, contentType);
    if (!res.ok || !result) {
      _lastUploadError = !res.ok
        ? (res.status === 401
            ? 'Your session expired. Please sign in again.'
            : res.status === 413
              ? `File is too large (max ${Math.round(endpoint.maxBytes / (1024 * 1024))} MB).`
              : `Upload failed (server error ${res.status})${json?.error ? `: ${json.error}` : (json?.message ? `: ${json.message}` : '')}`)
        : 'Upload succeeded but the server returned no file URL.';
      console.warn('[uploadFile] failed', { kind, status: res.status, json });
      return null;
    }
    _lastUploadError = null;
    return result;
  } catch (err: any) {
    _lastUploadError = `Network error: ${err?.message || String(err)}. Check your connection and try again.`;
    console.warn('[uploadFile] network error', err);
    return null;
  }
}

// Replays the most recent uploadFile/uploadMedia call (same uri + options) so a
// screen can offer a one-tap "Retry" after surfacing getLastUploadError(). Returns
// null if there is nothing to retry.
let _lastUploadInput: { uri: string; opts: Parameters<typeof uploadFile>[1] } | null = null;
export async function retryLastUpload(): Promise<UploadResult | null> {
  if (!_lastUploadInput) {
    _lastUploadError = 'Nothing to retry.';
    return null;
  }
  return uploadFile(_lastUploadInput.uri, _lastUploadInput.opts);
}

// --- Backward-compatible thin wrappers ---------------------------------------

// Legacy entry point: upload a local image/media URI and return just the public
// URL (or null). Existing call sites depend on this exact signature. Routes to
// /uploads/media (the 100MB image/video/audio bucket).
export async function uploadMedia(uri: string): Promise<string | null> {
  const result = await uploadFile(uri, { kind: 'media' });
  return result?.url ?? null;
}

// Profile avatar: routes to /uploads/avatar (5MB, server resizes to 512²).
export async function uploadAvatar(uri: string): Promise<string | null> {
  const result = await uploadFile(uri, { kind: 'avatar' });
  return result?.url ?? null;
}

// Message/post attachment: routes to /uploads/attachment (50MB) and returns the
// full envelope so callers can build POST /messages attachments[] = {url,filename,
// size,contentType}.
export async function uploadAttachment(uri: string): Promise<UploadResult | null> {
  return uploadFile(uri, { kind: 'attachment' });
}

// Per-channel upload via POST /channels/:channelId/upload (single file, 10MB,
// image + mp4/webm). Response shape is the simpler { success, data:{ url, filename,
// contentType, size } }. Uses the shared api()-style auth/refresh by going through
// fetch directly (channel route is NOT under /uploads/*).
export async function uploadChannelFile(channelId: string, uri: string): Promise<UploadResult | null> {
  const described = describeFile(uri);
  // Same HEIC→JPEG + downscale guard as uploadFile so channel image attachments
  // render for every member, not just the iOS uploader.
  const { uri: sendUri, filename, contentType } =
    await transcodeForUpload(uri, described.filename, described.contentType);
  const path = `/channels/${channelId}/upload`;
  const buildForm = () => {
    const fd = new FormData();
    fd.append('file', { uri: sendUri, name: filename, type: contentType } as any);
    return fd;
  };
  _lastUploadInput = null; // channel uploads are not part of the uploadFile retry chain

  try {
    let token = await getToken();
    let { res, json } = await uploadAttempt(`${API_BASE}${path}`, token, buildForm);
    if (res.status === 401 && token) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        ({ res, json } = await uploadAttempt(`${API_BASE}${path}`, newToken, buildForm));
      } else {
        await clearToken();
        emitAuthChange(false);
      }
    }
    const result = extractUploadResult(json, filename, contentType);
    if (!res.ok || !result) {
      _lastUploadError = !res.ok
        ? `Upload failed (server error ${res.status})${json?.error ? `: ${json.error}` : ''}`
        : 'Upload succeeded but the server returned no file URL.';
      console.warn('[uploadChannelFile] failed', { channelId, status: res.status, json });
      return null;
    }
    _lastUploadError = null;
    return result;
  } catch (err: any) {
    _lastUploadError = `Network error: ${err?.message || String(err)}. Check your connection and try again.`;
    console.warn('[uploadChannelFile] network error', err);
    return null;
  }
}

// --- Encrypted media upload ---------------------------------------------------

// Uploads an already-encrypted (ciphertext) local file to /uploads/encrypted and
// returns a public, client-accessible URL (or null). The backend stores the blob
// OPAQUELY (no type-check/processing/scan), so we send it as a generic
// application/octet-stream — the real media type is carried out-of-band in the
// E2E marker, never to the server. Mirrors uploadMedia's refresh-on-401 retry and
// toPublicMediaUrl rewrite so encrypted media survives the access-token lifetime
// and never leaks an internal MinIO host.
export async function uploadEncrypted(localUri: string): Promise<string | null> {
  // Single-use FormData (file stream consumed on send) — rebuild per attempt. The
  // name/type are deliberately generic: the ciphertext is opaque to the server.
  const buildForm = () => {
    const fd = new FormData();
    fd.append('file', { uri: localUri, name: 'blob.bin', type: 'application/octet-stream' } as any);
    return fd;
  };

  const attempt = async (token: string | null) => {
    // Do NOT set Content-Type — fetch must add the multipart boundary itself.
    const res = await fetch(`${API_BASE}/uploads/encrypted`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: buildForm(),
    });
    let json: any = null;
    try { json = await res.json(); } catch { json = null; }
    return { res, json };
  };

  try {
    let token = await getToken();
    let { res, json } = await attempt(token);

    if (res.status === 401 && token) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        ({ res, json } = await attempt(newToken));
      } else {
        await clearToken();
      }
    }

    const raw =
      json?.data?.original?.url ||
      json?.data?.url ||
      json?.data?.fileUrl ||
      json?.url ||
      json?.fileUrl ||
      '';
    const url = toPublicMediaUrl(raw);
    if (!res.ok || !url) {
      console.warn('[uploadEncrypted] upload failed', { status: res.status, json });
    }
    return res.ok && url ? url : null;
  } catch (err) {
    console.warn('[uploadEncrypted] network error', err);
    return null;
  }
}

// Public media origin + the live nginx proxy prefix that fronts MinIO.
//   nginx (sites-enabled/cryb): location ~ ^/media/(cryb-avatars|cryb-stories|
//   cryb-thumbnails|cryb-media|avatars|media)/(.*)$  →  proxy_pass 127.0.0.1:9000
// So the ONLY externally-reachable shape for an uploaded object is
//   https://api.cryb.ai/media/<bucket>/<object>
// and PUBLIC_MEDIA_URL on the server is set to exactly "https://api.cryb.ai/media".
const PUBLIC_MEDIA_ORIGIN = 'https://api.cryb.ai';
const PUBLIC_MEDIA_PREFIX = `${PUBLIC_MEDIA_ORIGIN}/media`;

// Internal MinIO hosts the backend may leak into a url when PUBLIC_MEDIA_URL is
// unset or a fallback path runs. None of these resolve off the server, so an
// <Image> on ANOTHER user's device (no auth header, different network) renders a
// broken thumbnail — the exact "B can't see A's image" propagation bug. We
// normalise every such shape to the public /media/<bucket>/<object> proxy.
const INTERNAL_MEDIA_HOSTS = [
  'http://127.0.0.1:9000', 'https://127.0.0.1:9000',
  'http://localhost:9000', 'https://localhost:9000',
  'http://127.0.0.1:9500', 'http://localhost:9500',
  'http://minio:9000', 'https://minio:9000',
  'http://minio:9500',
];

// Rewrites ANY internal/relative/loopback MinIO URL to the public nginx proxy so
// media is reachable by EVERY user's device (not just the uploader / not just the
// server). Idempotent: an already-public https://api.cryb.ai/media/... URL passes
// through unchanged. Handles:
//   - loopback/docker MinIO origins  → swap origin for the /media proxy
//   - bare-relative bucket paths      (/cryb-media/x.jpg)  → prefix with /media
//   - protocol-relative / api-root    (https://api.cryb.ai/cryb-media/x.jpg) → /media
//   - already-correct /media/ URLs    → unchanged
export function toPublicMediaUrl(url: string): string {
  if (!url || typeof url !== 'string') return url;
  let u = url.trim();

  // 1) Already the public proxy → leave it alone.
  if (u.startsWith(`${PUBLIC_MEDIA_PREFIX}/`)) return u;

  // Track whether what remains is a raw "<bucket>/<object>" path (from stripping an
  // internal origin / a bare relative url) vs. a path that already carries the
  // /media proxy prefix. A bucket literally NAMED "media" must NOT be mistaken for
  // the proxy prefix — that's the only way to tell them apart.
  let pathIsBucketRooted = false;

  // 2) Strip a known internal MinIO origin down to "/bucket/object".
  for (const host of INTERNAL_MEDIA_HOSTS) {
    if (u.startsWith(host + '/')) {
      u = u.slice(host.length); // keep the leading slash + path
      pathIsBucketRooted = true;
      break;
    }
  }

  // 3) An absolute api.cryb.ai URL pointing straight at a bucket root (NOT already
  //    under /media or /cdn) — a fallback that used the api origin as the media
  //    origin. Reduce to the bucket-rooted path so step 5 re-homes it under /media.
  if (u.startsWith(`${PUBLIC_MEDIA_ORIGIN}/`)) {
    const path = u.slice(PUBLIC_MEDIA_ORIGIN.length); // "/<...>"
    if (/^\/(cryb-[a-z]+|avatars|media|stories|thumbnails|banners|posts|nfts|uploads)\//.test(path)) {
      u = path;
      pathIsBucketRooted = true;
    } else {
      return u; // some other api.cryb.ai URL (e.g. /cdn/...) — don't touch
    }
  }

  // 4) Any OTHER absolute http(s) URL we don't recognise → trust it as-is (could
  //    be a real external CDN). Only relative/bucket-rooted paths fall through.
  if (/^https?:\/\//.test(u)) return u;

  // 5) Now u is a path. Normalise to exactly one /media/<bucket>/<object>.
  if (!u.startsWith('/')) {
    u = '/' + u;
    pathIsBucketRooted = true; // a bare "bucket/object" relative url
  }
  // Only honour an existing /media/ prefix when it DIDN'T come from a bucket root —
  // otherwise a bucket named "media" (/media/<object>) would skip re-homing and
  // 404 (it needs to become /media/media/<object>).
  if (!pathIsBucketRooted && u.startsWith('/media/')) return `${PUBLIC_MEDIA_ORIGIN}${u}`;
  return `${PUBLIC_MEDIA_PREFIX}${u}`;
}
